import { defineStore } from "pinia";
import type { PersistenceOptions } from "pinia-plugin-persistedstate";

import { useAccessInfo } from "@/composition/api/authorization.ts";
import { LayoutMode } from "@/types/enums.ts";
import type { Project } from "@/types/globalTypes";
import type { GetUserPermissionsApi } from "@/types/permissions";

declare module "pinia" {
  export interface PiniaCustomProperties {
    persist: PersistenceOptions;
  }
}

export interface State {
  preSignUpFetchedAt: null | Date;
  userPermissions: GetUserPermissionsApi | null;
  userPermissionsFetchedAt: number | null;
  termsOfServiceAgreementRequired: boolean | null;
  termsOfServiceVersionRequired: string | null;
  signUpRequired: boolean | null;
  activationPending: boolean | null;
  deactivated: boolean | null;
  createDate: null | string;
  activationDate: null | string;
  validToDate: string | null;
  config: {
    feedLayoutMode: LayoutMode;
    autoRefreshActive: boolean;
    cvdGalleryListView: boolean;
  };
  preferences: {
    preferredLanguage: "de" | "en" | "es" | null;
    preferredCvdPage: string | null;
    preferredHomepage: Project | null;
    suppressOnboardingTour: boolean | null;
  };
}

export const useUserStore = defineStore("user", {
  state: (): State => ({
    preSignUpFetchedAt: null,
    userPermissions: null,
    userPermissionsFetchedAt: null,
    termsOfServiceAgreementRequired: null,
    termsOfServiceVersionRequired: null,
    signUpRequired: null,
    activationPending: null,
    deactivated: null,
    createDate: null,
    activationDate: null,
    validToDate: null,
    config: {
      feedLayoutMode: LayoutMode.STANDARD,
      autoRefreshActive: true,
      cvdGalleryListView: false,
    },
    preferences: {
      preferredLanguage: null,
      preferredCvdPage: null,
      preferredHomepage: null,
      suppressOnboardingTour: true,
    },
  }),
  actions: {
    async fetchUserPermissions() {
      const now = Date.now();
      const { data, execute: getUserPermissions, error } = useAccessInfo();

      if (
        !this.userPermissions ||
        !this.userPermissionsFetchedAt ||
        now - this.userPermissionsFetchedAt > 5 * 60 * 1000
      ) {
        try {
          await getUserPermissions();
          // The afterFetch hook in useAccessInfo already updates userPermissions in the store.
          // We just need to update the timestamp and handle errors.
          if (error.value) {
            // eslint-disable-next-line no-console
            console.warn("Failed to fetch user permissions:", error.value);
            this.userPermissions = {
              organization: { gpNumber: "", id: 0, name: "" },
              permissions: [],
              roles: [],
              userGroups: { id: null, name: "" },
            };
          } else if (data.value) {
            // Data is already set by afterFetch, but we ensure it's valid
            this.userPermissions = data.value;
          }

          this.userPermissionsFetchedAt = now;
        } catch (catchError) {
          // eslint-disable-next-line no-console -- just for error logs
          console.error("Error fetching user permissions:", catchError);
          this.userPermissions = {
            organization: { gpNumber: "", id: 0, name: "" },
            permissions: [],
            roles: [],
            userGroups: { id: null, name: "" },
          };
          this.userPermissionsFetchedAt = now;
        }
      }

      return this.userPermissions;
    },
    clearUserData() {
      this.userPermissions = null;
      this.userPermissionsFetchedAt = null;
    },
  },
  persist: {
    pick: ["config"],
  },
});
